import { tablesDB, DATABASE_ID, USERS_TABLE_ID, STORE_TABLE_ID, ORDERS_TABLE_ID, PRODUCTS_TABLE_ID, REQUESTS_TABLE_ID, Query } from '../lib/appwrite';
import { filterByTimeRange, getBucketsForTimeRange, getDateKeyForBucket } from '../utils/timeRange';

/**
 * Detailed platform-wide product analytics
 * @param {Object} options - { timeRange: 'day'|'week'|'month'|'quarter'|'year' }
 */
export async function getPlatformProductAnalytics(options = {}) {
  const { timeRange = 'month' } = options;

  const [productsRes, ordersRes, storesRes] = await Promise.all([
    tablesDB.listRows(DATABASE_ID, PRODUCTS_TABLE_ID, [Query.limit(5000)]),
    tablesDB.listRows(DATABASE_ID, ORDERS_TABLE_ID, [Query.limit(5000)]),
    tablesDB.listRows(DATABASE_ID, STORE_TABLE_ID, [Query.limit(500)]),
  ]);

  const products = productsRes.rows || [];
  const allOrders = ordersRes.rows || [];
  const orders = filterByTimeRange(allOrders, timeRange);
  const stores = storesRes.rows || [];

  const storeMap = stores.reduce((acc, s) => {
    acc[s.$id] = s.storeName || s.$id;
    return acc;
  }, {});

  const productSales = {};
  orders.forEach((o) => {
    const name = o.productName || 'Unknown';
    const pid = o.productId || name;
    const key = pid;
    if (!productSales[key]) {
      productSales[key] = {
        productId: o.productId,
        productName: name,
        productRowId: null,
        storeId: o.storeId,
        category: null,
        unitsSold: 0,
        revenue: 0,
        orderCount: 0,
      };
    }
    productSales[key].unitsSold += parseInt(o.quantity, 10) || 0;
    productSales[key].revenue += parseFloat(o.totalAmount) || 0;
    productSales[key].orderCount += 1;
  });

  products.forEach((p) => {
    const key = p.productId || p.productName || p.$id;
    if (!productSales[key]) {
      productSales[key] = {
        productId: p.productId,
        productName: p.productName || 'Unnamed',
        productRowId: p.$id,
        storeId: p.storeId,
        category: p.productCategory,
        unitsSold: 0,
        revenue: 0,
        orderCount: 0,
      };
    } else {
      productSales[key].productRowId = p.$id;
      productSales[key].category = p.productCategory || productSales[key].category;
      productSales[key].storeId = productSales[key].storeId || p.storeId;
    }
  });

  const byCategory = {};
  const byStore = {};
  let totalProductRevenue = 0;
  let totalUnitsSold = 0;
  const lowStock = [];
  const outOfStock = [];

  products.forEach((p) => {
    const cat = p.productCategory || 'Uncategorized';
    if (!byCategory[cat]) byCategory[cat] = { count: 0, revenue: 0, units: 0, products: [] };
    byCategory[cat].count += 1;
    byCategory[cat].products.push(p);

    const sid = p.storeId || 'unknown';
    if (!byStore[sid]) byStore[sid] = { count: 0, storeName: storeMap[sid] || sid };
    byStore[sid].count += 1;

    const stock = parseInt(p.stockCount, 10) || 0;
    if (stock === 0) outOfStock.push({ ...p, storeName: storeMap[p.storeId] });
    else if (stock < 50) lowStock.push({ ...p, storeName: storeMap[p.storeId] });
  });

  Object.values(productSales).forEach((ps) => {
    totalProductRevenue += ps.revenue;
    totalUnitsSold += ps.unitsSold;
    const cat = ps.category || 'Uncategorized';
    if (!byCategory[cat]) byCategory[cat] = { count: 0, revenue: 0, units: 0, products: [] };
    byCategory[cat].revenue += ps.revenue;
    byCategory[cat].units += ps.unitsSold;
  });

  const salesArray = Object.values(productSales).map((ps) => ({
    ...ps,
    storeName: storeMap[ps.storeId] || ps.storeId,
  }));

  // Platform-wide top products: aggregate by product name (ignore store)
  const byProductName = {};
  salesArray.forEach((ps) => {
    const key = (ps.productName || 'Unknown').trim().toLowerCase();
    if (!byProductName[key]) {
      byProductName[key] = {
        productName: ps.productName || 'Unknown',
        productRowId: ps.productRowId,
        category: ps.category,
        unitsSold: 0,
        revenue: 0,
        orderCount: 0,
      };
    }
    byProductName[key].unitsSold += ps.unitsSold;
    byProductName[key].revenue += ps.revenue;
    byProductName[key].orderCount += ps.orderCount;
    if (ps.productRowId) byProductName[key].productRowId = ps.productRowId;
    if (ps.category) byProductName[key].category = ps.category;
  });
  const platformTop = Object.values(byProductName);
  const topByRevenue = [...platformTop].sort((a, b) => b.revenue - a.revenue).slice(0, 10);
  const topByUnits = [...platformTop].sort((a, b) => b.unitsSold - a.unitsSold).slice(0, 10);
  const topByOrders = [...platformTop].sort((a, b) => b.orderCount - a.orderCount).slice(0, 10);

  const bucketConfig = getBucketsForTimeRange(timeRange);
  const byMonth = {};
  bucketConfig.forEach((b) => {
    byMonth[b.key] = { label: b.label, orders: 0, revenue: 0, units: 0 };
  });
  orders.forEach((o) => {
    const dt = o.orderDate || o.$createdAt || 0;
    const key = getDateKeyForBucket(dt, timeRange);
    if (key && byMonth[key]) {
      byMonth[key].orders += 1;
      byMonth[key].revenue += parseFloat(o.totalAmount) || 0;
      byMonth[key].units += parseInt(o.quantity, 10) || 0;
    } else if (key) {
      byMonth[key] = byMonth[key] || { label: key, orders: 0, revenue: 0, units: 0 };
      byMonth[key].orders += 1;
      byMonth[key].revenue += parseFloat(o.totalAmount) || 0;
      byMonth[key].units += parseInt(o.quantity, 10) || 0;
    }
  });

  const categoryList = Object.entries(byCategory).map(([name, data]) => ({
    name,
    ...data,
  }));

  const storeList = Object.entries(byStore)
    .map(([id, data]) => ({ storeId: id, storeName: data.storeName, count: data.count }))
    .sort((a, b) => b.count - a.count);

  const monthEntries = Object.entries(byMonth).map(([k, v]) => ({ key: k, ...v }));
  const lastMonth = monthEntries[monthEntries.length - 1];
  const prevMonth = monthEntries[monthEntries.length - 2];
  const revenueGrowth = prevMonth && prevMonth.revenue > 0
    ? ((lastMonth.revenue - prevMonth.revenue) / prevMonth.revenue) * 100
    : 0;
  const ordersGrowth = prevMonth && prevMonth.orders > 0
    ? ((lastMonth.orders - prevMonth.orders) / prevMonth.orders) * 100
    : 0;

  const avgOrderValue = orders.length > 0 ? totalProductRevenue / orders.length : 0;
  const productsWithZeroSales = products.filter((p) => {
    const key = p.productId || p.productName || p.$id;
    const sales = productSales[key];
    return !sales || (sales.unitsSold === 0 && sales.revenue === 0);
  });
  const categoriesWithZeroRevenue = categoryList.filter((c) => (c.revenue || 0) === 0);
  const worstCategories = [...categoryList].sort((a, b) => a.revenue - b.revenue).slice(0, 5);

  const insights = [];
  if (outOfStock.length > 0) {
    insights.push({ type: 'danger', title: 'Out of stock', count: outOfStock.length, action: 'Restock immediately to avoid lost sales.' });
  }
  if (lowStock.length > 0) {
    insights.push({ type: 'warn', title: 'Low stock', count: lowStock.length, action: 'Plan restocking to prevent stockouts.' });
  }
  if (productsWithZeroSales.length > 0) {
    insights.push({ type: 'info', title: 'Products with zero sales', count: productsWithZeroSales.length, action: 'Review pricing, visibility, or consider promotions.' });
  }
  if (categoriesWithZeroRevenue.length > 0) {
    insights.push({ type: 'info', title: 'Categories with zero revenue', count: categoriesWithZeroRevenue.length, action: 'Consider marketing or adding products to these categories.' });
  }
  if (revenueGrowth < -10) {
    insights.push({ type: 'warn', title: 'Revenue decline', value: `${revenueGrowth.toFixed(1)}%`, action: 'Revenue down vs last month. Check pricing and demand.' });
  } else if (revenueGrowth > 10) {
    insights.push({ type: 'success', title: 'Revenue growth', value: `+${revenueGrowth.toFixed(1)}%`, action: 'Revenue up vs last month. Double down on what\'s working.' });
  }

  return {
    summary: {
      totalProducts: products.length,
      totalCategories: categoryList.length,
      totalStoresWithProducts: Object.keys(byStore).length,
      totalProductRevenue,
      totalUnitsSold,
      totalOrders: orders.length,
      lowStockCount: lowStock.length,
      outOfStockCount: outOfStock.length,
    },
    byCategory: categoryList.sort((a, b) => b.revenue - a.revenue),
    byStore: storeList,
    topByRevenue,
    topByUnits,
    topByOrders,
    lowStock,
    outOfStock,
    byMonth: monthEntries,
    storeMap,
    insights,
    performance: {
      avgOrderValue,
      revenueGrowth,
      ordersGrowth,
      productsWithZeroSales: productsWithZeroSales.length,
      categoriesWithZeroRevenue: categoriesWithZeroRevenue.length,
      worstCategories,
    },
  };
}

export async function getDashboardStats(options = {}) {
  const { timeRange = 'month' } = options;

  const [usersRes, storesRes, ordersRes, requestsRes, productsRes] = await Promise.all([
    tablesDB.listRows(DATABASE_ID, USERS_TABLE_ID, [Query.limit(5000)]),
    tablesDB.listRows(DATABASE_ID, STORE_TABLE_ID, [Query.limit(5000)]),
    tablesDB.listRows(DATABASE_ID, ORDERS_TABLE_ID, [Query.limit(5000)]),
    tablesDB.listRows(DATABASE_ID, REQUESTS_TABLE_ID, [Query.limit(500)]),
    tablesDB.listRows(DATABASE_ID, PRODUCTS_TABLE_ID, [Query.limit(5000)]),
  ]);

  const users = usersRes.rows || [];
  const stores = storesRes.rows || [];
  const allOrders = ordersRes.rows || [];
  const requests = requestsRes.rows || [];
  const products = productsRes.rows || [];
  const orders = timeRange ? filterByTimeRange(allOrders, timeRange) : allOrders;

  const byRole = (...roles) =>
    users.filter((u) => roles.includes((u.roles || u.role || '').toLowerCase())).length;

  const totalRevenue = orders.reduce((sum, o) => sum + (parseFloat(o.totalAmount) || 0), 0);
  const uniqueOrderIds = new Set(orders.map((o) => o.orderId || o.$id));
  const storeMapForProducts = stores.reduce((acc, s) => { acc[s.$id] = s.storeName || s.$id; return acc; }, {});

  const lowStockProducts = products.filter((p) => {
    const s = parseInt(p.stockCount, 10) || 0;
    return s > 0 && s < 50;
  });
  const outOfStockProducts = products.filter((p) => (parseInt(p.stockCount, 10) || 0) === 0);
  const lowStock = lowStockProducts.length;

  const lowStockList = lowStockProducts.slice(0, 10).map((p) => ({
    $id: p.$id,
    productName: p.productName || 'Unnamed',
    stockCount: parseInt(p.stockCount, 10) || 0,
    storeName: storeMapForProducts[p.storeId] || p.storeId || '—',
    storeId: p.storeId,
  }));
  const outOfStockList = outOfStockProducts.slice(0, 10).map((p) => ({
    $id: p.$id,
    productName: p.productName || 'Unnamed',
    storeName: storeMapForProducts[p.storeId] || p.storeId || '—',
    storeId: p.storeId,
  }));

  const pendingRequests = requests.filter((r) =>
    ['pending', 'new'].includes((r.status || '').toLowerCase())
  );
  const deliveryCompleted = requests.filter((r) =>
    ['completed', 'delivered'].includes((r.status || '').toLowerCase())
  ).length;
  const deliveryPending = pendingRequests.length;
  const pendingDeliveriesList = pendingRequests.slice(0, 10).map((r) => ({
    $id: r.$id,
    packageName: r.packageName || r.orderId || 'Package',
    storeName: r.storeName || storeMapForProducts[r.storeId] || r.storeId || '—',
    orderId: r.orderId,
    status: r.status,
  }));
  const deliveryInProgress = requests.filter((r) =>
    ['in_progress', 'picked_up', 'delivering', 'on_the_way'].some((s) =>
      (r.status || '').toLowerCase().includes(s)
    )
  ).length;

  const bucketConfig = getBucketsForTimeRange(timeRange);
  const byMonth = {};
  bucketConfig.forEach((b) => { byMonth[b.key] = { label: b.label, orders: 0, revenue: 0 }; });
  orders.forEach((o) => {
    const dt = o.orderDate || o.$createdAt || 0;
    const key = getDateKeyForBucket(dt, timeRange);
    if (key && byMonth[key]) {
      byMonth[key].orders += 1;
      byMonth[key].revenue += parseFloat(o.totalAmount) || 0;
    }
  });
  const orderTrend = bucketConfig.map((b) => ({
    label: b.label,
    orders: byMonth[b.key]?.orders ?? 0,
    revenue: byMonth[b.key]?.revenue ?? 0,
  }));

  const productCategoryMap = products.reduce((acc, p) => {
    const key = (p.productId || p.productName || p.$id || '').toString();
    if (key) acc[key] = p.productCategory || 'Uncategorized';
    if (p.productName) acc[(p.productName || '').trim().toLowerCase()] = p.productCategory || 'Uncategorized';
    return acc;
  }, {});

  const byProductName = {};
  const byStore = {};
  const byCategory = {};
  const deliveryMethodByOrderId = {};
  const orderStatusByOrderId = {};
  const storeMap = stores.reduce((acc, s) => { acc[s.$id] = s.storeName || s.$id; return acc; }, {});
  let totalUnitsSold = 0;

  orders.forEach((o) => {
    const name = (o.productName || 'Unknown').trim();
    if (!byProductName[name]) byProductName[name] = { revenue: 0, quantity: 0 };
    byProductName[name].revenue += parseFloat(o.totalAmount) || 0;
    byProductName[name].quantity += parseInt(o.quantity, 10) || 0;
    totalUnitsSold += parseInt(o.quantity, 10) || 0;

    const sid = o.storeId || 'unknown';
    if (!byStore[sid]) byStore[sid] = { storeName: storeMap[sid] || sid, revenue: 0, orderCount: 0 };
    byStore[sid].revenue += parseFloat(o.totalAmount) || 0;
    byStore[sid].orderCount += 1;

    const cat = productCategoryMap[o.productId] || productCategoryMap[name?.toLowerCase()] || 'Uncategorized';
    if (!byCategory[cat]) byCategory[cat] = { revenue: 0, quantity: 0 };
    byCategory[cat].revenue += parseFloat(o.totalAmount) || 0;
    byCategory[cat].quantity += parseInt(o.quantity, 10) || 0;

    const oid = o.orderId || o.$id;
    if (!orderStatusByOrderId[oid]) {
      orderStatusByOrderId[oid] = (o.status || 'unknown').toLowerCase();
    }
    if (!deliveryMethodByOrderId[oid]) {
      deliveryMethodByOrderId[oid] = (o.deliveryMethod || 'unknown').trim().toLowerCase() || 'unknown';
    }
  });

  const byDeliveryMethod = {};
  Object.values(deliveryMethodByOrderId).forEach((dm) => {
    byDeliveryMethod[dm] = (byDeliveryMethod[dm] || 0) + 1;
  });

  const topCategories = Object.entries(byCategory)
    .map(([name, d]) => ({ name, ...d }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const deliveryMethodUsage = Object.entries(byDeliveryMethod)
    .filter(([k]) => k !== 'unknown')
    .sort((a, b) => b[1] - a[1]);

  const byOrderStatus = {};
  Object.values(orderStatusByOrderId).forEach((status) => {
    byOrderStatus[status] = (byOrderStatus[status] || 0) + 1;
  });

  const topProducts = Object.entries(byProductName)
    .map(([name, d]) => ({ name, ...d }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const topStores = Object.entries(byStore)
    .map(([id, d]) => ({ storeId: id, ...d }))
    .filter((s) => s.storeId !== 'unknown')
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const orderStatusBreakdown = Object.entries(byOrderStatus)
    .filter(([k]) => k !== 'unknown')
    .sort((a, b) => b[1] - a[1]);

  const uniqueOrderCount = uniqueOrderIds.size;
  const avgOrderValue = uniqueOrderCount > 0 ? totalRevenue / uniqueOrderCount : 0;

  const lastB = orderTrend[orderTrend.length - 1];
  const prevB = orderTrend[orderTrend.length - 2];
  const revenueGrowth = prevB && prevB.revenue > 0
    ? ((lastB?.revenue ?? 0) - prevB.revenue) / prevB.revenue * 100
    : 0;
  const ordersGrowth = prevB && prevB.orders > 0
    ? ((lastB?.orders ?? 0) - prevB.orders) / prevB.orders * 100
    : 0;

  return {
    totalUsers: users.length,
    sellers: byRole('seller'),
    buyers: byRole('customer', 'buyer'),
    drivers: byRole('driver'),
    admins: byRole('admin'),
    totalStores: stores.length,
    activeStores: stores.filter((s) => s.isActive).length,
    totalProducts: products.length,
    totalOrders: uniqueOrderIds.size,
    totalRevenue,
    totalUnitsSold,
    lowStockCount: lowStock,
    outOfStockCount: outOfStockProducts.length,
    lowStockList,
    outOfStockList,
    pendingDeliveriesList,
    deliveryTotal: requests.length,
    deliveryCompleted,
    deliveryPending,
    deliveryInProgress,
    orderTrend,
    topProducts,
    topStores,
    topCategories,
    orderStatusBreakdown,
    deliveryMethodUsage,
    avgOrderValue,
    revenueGrowth,
    ordersGrowth,
    prevPeriodOrders: prevB?.orders ?? 0,
    prevPeriodRevenue: prevB?.revenue ?? 0,
    lastPeriodOrders: lastB?.orders ?? 0,
    lastPeriodRevenue: lastB?.revenue ?? 0,
  };
}

